import { action, computed, observable } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';

import { AppManager, Variables, Input } from 'core/index';

import { FormStore } from 'libs/forms/app';

import { modalStore } from 'apps/modals/app';
import { clientStore } from 'apps/client/app';

import { ContributionForm } from 'apps/repository/views/contributionForm';
import { conf } from 'apps/repository/config.yaml';
import { ConflictsArrayApp, ConflictModel } from 'apps/repository/app';
import { ProjectApp, ProjectModel } from 'apps/project/app';
import {
  ConflictsQuery,
  CommitContributionMutation,
} from 'apps/repository/gql';

export class ConflictsManager extends AppManager {
  @observable activeConflict = 0;
  @observable resolution = observable.map();
  @observable contributionTitle = '';
  @observable contributionDescription = '';

  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('conflicts', ConflictsArrayApp);
    this.AppRef('project', ProjectApp);

    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
    });

    this.variables.onChange(['space', 'slug'], this.ConflictsQuery);
  }

  @computed
  get loading() {
    return this.queries.get('conflicts')?.loading;
  }

  @computed
  get allConflictsResolved() {
    return this.resolution.size === this.conflicts.length;
  }

  @action
  ConflictsQuery = () => {
    const q = new ConflictsQuery(this, {
      queryKey: 'conflicts',
      onSysErr: this.onQError,
      fetchPolicy: 'no-cache',
    });
    q.process(
      conflicts => {
        if (conflicts !== null) {
          return conflicts.map(conflict => ConflictModel.Load(conflict));
        }
        return [];
      },
      this.UpdateConflicts,
      { subPath: 'conflicts' },
    );
    q.process(project => ProjectModel.Load(project), this.UpdateProject);
    q.query();
  };

  @action
  UpdateConflicts = conflictModels => {
    this.conflicts = conflictModels;
    if (!this.loading && this.conflicts.apps.length === 0) {
      clientStore.routerStore.navigate(
        `${clientStore.routerStore.route.name.replace('.conflicts', '.files')}`,
        this.params.toJSON(),
        { replace: true },
      );
      // hacky soltion to fix cancelling of queries after re-direct.
      // can be fixed when re-factoring project page.
      window.location.reload();
    } else {
      clientStore.context.set('conflictsManager', this);
    }
  };

  @action UpdateProject = projectModel => {
    this.project = projectModel;
  };

  @action
  SetActiveConflict = index => {
    this.activeConflict = this.apps[index];
  };

  @action
  SetResolution = (actionType, conflict, fileId, conflictIndex) => {
    if (actionType === 'pick') {
      this.resolution.set(conflict.path, fileId);
    } else if (actionType === 'delete') {
      this.resolution.set(conflict.path, null, conflictIndex);
    }
    conflict.SetMarkedResolved(true);
    if (this.conflicts.length !== conflictIndex + 1) {
      this.NextConflict(conflictIndex);
    } else {
      this.Finalise();
    }
  };

  @action
  NextConflict = currentConflictIndex => {
    this.activeConflict = currentConflictIndex + 1;
  };

  SetActiveConflict = conflictIndex => {
    this.activeConflict = conflictIndex;
  };

  @action
  Finalise = () => {
    if (this.allConflictsResolved) {
      this.form = new FormStore(this, conf, 'contributionForm');
      modalStore.SetModalComponent(ContributionForm, { manager: this });
    }
  };

  @action
  CommitContribution = () => {
    const resObj = this.resolution.toJSON();
    const input = new Input({
      projectId: this.project.id,
      title: () => this.contributionTitle,
      description: () => this.contributionDescription,
      conflictsParent: this.project.conflictsParent,
      conflictsResolution: Object.keys(resObj).map(res => {
        return {
          path: res,
          pickedFileId: resObj[res],
        };
      }),
    });
    this.mutations.push(
      CommitContributionMutation.New(input, {
        onSuccess: this.onCommitSuccess,
        onSysErr: this.onCommitError,
        onUserErr: this.onCommitError,
      }),
    );
  };

  @action
  onCommitSuccess = () => {
    this.ConflictsQuery();
    this.form.clearDraft();
    modalStore.Close();
    Alert.success(i18next.t('Conflict resolution successful'));
  };

  @action
  onCommitError = () => {
    Alert.error(
      i18next.t('Hmm... The conflict resolution failed. Please try again.'),
    );
  };
}
